import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Button, Space, Select, Switch, message, Tooltip, Dropdown, Drawer, Checkbox, Tag, Badge, Divider } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FileTextOutlined,
  BarChartOutlined,
  HistoryOutlined,
  CopyOutlined,
  EditOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import AIPanel from '../components/AIPanel';
import ChartStyleEditor, { ChartStyleOverrides } from '../components/ChartStyleEditor';
import { generateChart, recommendCharts, getColumns } from '../services/api';
import {
  CHART_ICONS, CHART_LABELS, CATEGORIES,
  applyStyleOverrides, injectDataZoom,
  generateHtmlReport, downloadHtml,
  handleCopy4K as handleCopy4KUtil,
  chartActionBtnStyle, safeParseParams,
} from '../utils/chartUtils';
import type { ChartConfig, AIExplanation, ColumnInfo } from '../types';

const chartTypes = Object.entries(CHART_LABELS).map(([value, label]) => ({ value, label }));

const defaultExplanation: AIExplanation = {
  summary: '基于上传数据的分析，系统自动生成了可视化图表。以下是数据的关键特征和发现。',
  data_overview: [
    { metric: '数据行数', value: '正在计算...' },
    { metric: '数据列数', value: '正在计算...' },
    { metric: '数据质量', value: '良好' },
  ],
  key_findings: [
    '数据分布呈现明显的趋势特征',
    '数据集中存在若干显著的异常值',
    '变量之间存在一定的相关性',
  ],
  recommendations: [
    '建议进一步分析数据的统计分布特征',
    '可考虑使用统计分析模块进行深入分析',
    '关注异常值可能反映的业务问题',
  ],
};

interface HistoryItem {
  type: string;
  label: string;
  timestamp: number;
}

// ---- Generate multi-chart configs ----
function generateMultiChartConfigs(
  baseConfig: ChartConfig,
  columns: string[],
  chartType: string
): ChartConfig[] {
  const charts: ChartConfig[] = [];
  const option = baseConfig.option;

  if (option.series && Array.isArray(option.series) && option.series.length > 1) {
    option.series.forEach((s: any, idx: number) => {
      const singleOption: Record<string, any> = { ...option, series: [s] };
      if (singleOption.legend) {
        singleOption.legend = undefined;
      }
      charts.push({
        title: s.name || `图表 ${idx + 1}`,
        type: chartType,
        option: singleOption,
      });
    });
  }

  if (charts.length === 0) {
    charts.push(baseConfig);
  }

  return charts;
}

// Default style overrides
const DEFAULT_STYLE: ChartStyleOverrides = {
  colorTheme: 'default',
  titleFontSize: 18,
  labelFontSize: 12,
  legendPosition: 'top',
};

// ======================== Main Component ========================

const SmartChartResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawParams = searchParams.get('params') || '{}';
  const params = useMemo(() => safeParseParams(rawParams), [rawParams]);

  const [loading, setLoading] = useState(true);
  const [chartConfig, setChartConfig] = useState<ChartConfig | null>(null);
  const [explanation, setExplanation] = useState<AIExplanation>(defaultExplanation);
  const [currentType, setCurrentType] = useState(params.chartType || 'bar');
  const [isDemoMode, setIsDemoMode] = useState(false);
  // S5: AI timeout hint
  const [loadingHint, setLoadingHint] = useState('正在生成图表...');

  // Task 7: Per-chart style overrides (Map<index, StyleOverride>)
  // Index -1 is used for single-chart mode
  const [styleMap, setStyleMap] = useState<Record<number, ChartStyleOverrides>>({
    [-1]: { ...DEFAULT_STYLE },
  });

  const getStyle = useCallback((index: number): ChartStyleOverrides => {
    return styleMap[index] || { ...DEFAULT_STYLE };
  }, [styleMap]);

  const setStyleForChart = useCallback((index: number, style: ChartStyleOverrides) => {
    setStyleMap((prev) => ({ ...prev, [index]: style }));
  }, []);

  // Multi-chart mode
  const [multiMode, setMultiMode] = useState(false);
  const [editingChart, setEditingChart] = useState<number>(-999); // -999 = none, -1 = single chart, 0/1/2... = multi-chart index

  // Chart config cache & compatible types
  const [chartCache, setChartCache] = useState<Record<string, ChartConfig>>({});
  const [compatibleTypes, setCompatibleTypes] = useState<
    Array<{ chart_type: string; display_name: string; compatible: boolean; confidence: number }>
  >([]);

  // Chart history
  const [chartHistory, setChartHistory] = useState<HistoryItem[]>([
    {
      type: params.chartType || 'bar',
      label: CHART_LABELS[params.chartType || 'bar'] || '柱状图',
      timestamp: Date.now(),
    },
  ]);

  // ECharts refs for export
  const mainChartRef = useRef<any>(null);
  const multiChartRefs = useRef<Record<number, any>>({});

  // Column adjustment Drawer state
  const [colDrawerOpen, setColDrawerOpen] = useState(false);
  const [allColumns, setAllColumns] = useState<ColumnInfo[]>([]);
  const [colsLoading, setColsLoading] = useState(false);
  const [selectedCols, setSelectedCols] = useState<string[]>(params.columns || []);
  const [drawerXAxis, setDrawerXAxis] = useState<string>(params.xAxis || '');
  const [drawerYAxis, setDrawerYAxis] = useState<string>(params.yAxis || '');
  const [drawerGroupBy, setDrawerGroupBy] = useState<string>(params.groupBy || '');

  const handleOpenColDrawer = useCallback(async () => {
    setColDrawerOpen(true);
    if (allColumns.length === 0 && params.fileId) {
      setColsLoading(true);
      try {
        const cols = await getColumns(params.fileId);
        setAllColumns(cols);
      } catch {
        message.error('获取列信息失败');
      } finally {
        setColsLoading(false);
      }
    }
  }, [allColumns.length, params.fileId]);

  const handleApplyColChanges = useCallback(() => {
    if (selectedCols.length === 0) {
      message.warning('请至少选择一列');
      return;
    }
    const newParams = {
      ...params,
      columns: selectedCols,
      chartType: currentType,
      ...(drawerXAxis ? { xAxis: drawerXAxis } : {}),
      ...(drawerYAxis ? { yAxis: drawerYAxis } : {}),
      ...(drawerGroupBy ? { groupBy: drawerGroupBy } : {}),
    };
    if (!drawerXAxis) delete newParams.xAxis;
    if (!drawerYAxis) delete newParams.yAxis;
    if (!drawerGroupBy) delete newParams.groupBy;

    const encoded = encodeURIComponent(JSON.stringify(newParams));
    // Navigate to same page with new params to trigger full re-render
    navigate(`/smart-chart/result?params=${encoded}`, { replace: true });
    setColDrawerOpen(false);
    // Clear cache to force re-fetch
    setChartCache({});
    setLoading(true);
    message.success(`已更新为 ${selectedCols.length} 列，正在重新生成图表...`);
  }, [selectedCols, drawerXAxis, drawerYAxis, drawerGroupBy, params, currentType, navigate]);

  const TYPE_TAG_COLORS: Record<string, string> = {
    float: 'blue', float64: 'blue', integer: 'cyan', int64: 'cyan',
    categorical: 'green', object: 'green', datetime: 'orange', text: 'default',
  };

  // ---- Fetch compatible chart types on mount ----
  useEffect(() => {
    if (params.fileId && params.columns?.length) {
      recommendCharts(params.fileId, params.columns)
        .then((res) => {
          if (res.all_charts) {
            setCompatibleTypes(res.all_charts);
          }
        })
        .catch(() => {
          setCompatibleTypes(
            Object.entries(CHART_LABELS).map(([key, label]) => ({
              chart_type: key, display_name: label, compatible: true, confidence: 0.5,
            }))
          );
        });
    } else {
      setCompatibleTypes(
        Object.entries(CHART_LABELS).map(([key, label]) => ({
          chart_type: key, display_name: label, compatible: true, confidence: 0.5,
        }))
      );
    }
  }, [params.fileId, JSON.stringify(params.columns)]);

  // ---- Fetch chart config with caching ----
  useEffect(() => {
    if (chartCache[currentType]) {
      setChartConfig(chartCache[currentType]);
      setLoading(false);
      return;
    }

    const fetchChart = async () => {
      setLoading(true);
      setIsDemoMode(false);
      setLoadingHint('正在生成图表...');
      // S5: If loading > 5s, show extended hint
      const hintTimer = setTimeout(() => setLoadingHint('AI 分析中，请耐心等待...'), 5000);
      try {
        const config = await generateChart(params.fileId, currentType, params.columns, params.options);
        setChartConfig(config);
        setChartCache((prev) => ({ ...prev, [currentType]: config }));
        // 如果后端返回了 AI 解释，使用它；否则保留默认
        if (config.ai_explanation) {
          setExplanation(config.ai_explanation);
        } else {
          setExplanation((prev) => ({
            ...prev,
            data_overview: [
              { metric: '图表类型', value: config.type },
              { metric: '数据列', value: (params.columns || []).join(', ') },
            ],
          }));
        }
      } catch {
        setIsDemoMode(true);
        const demoOption = generateDemoChart(currentType);
        const demoConfig: ChartConfig = {
          title: `${CHART_LABELS[currentType] || currentType} 分析`,
          type: currentType,
          option: demoOption,
        };
        setChartConfig(demoConfig);
        setChartCache((prev) => ({ ...prev, [currentType]: demoConfig }));
        // S4: Use actual column/file info in fallback explanation
        const colInfo = (params.columns || []).join(', ') || '未知列';
        const fileInfo = params.fileId || '未知文件';
        setExplanation({
          summary: `当前展示为示例图表，实际数据文件 (${fileInfo}) 的列 [${colInfo}] 未能成功加载。请检查后端服务和数据文件是否有效。`,
          data_overview: [
            { metric: '图表类型', value: CHART_LABELS[currentType] || currentType },
            { metric: '数据来源', value: fileInfo },
            { metric: '选择的列', value: `${(params.columns || []).length} 列` },
            { metric: '状态', value: '示例数据' },
          ],
          key_findings: [
            `文件 ${fileInfo} 的数据未能成功获取，当前显示示例图表`,
            `选择的列: ${colInfo}`,
            '请确认后端服务已启动且数据文件未过期',
          ],
          recommendations: [
            '检查后端服务是否正常运行',
            '尝试重新上传数据文件',
            '确认所选列在数据中存在',
          ],
        });
      } finally {
        clearTimeout(hintTimer);
        setLoading(false);
      }
    };
    fetchChart();
  }, [currentType, rawParams]);

  // ---- Chart type switch handler ----
  const handleTypeChange = useCallback((newType: string) => {
    setCurrentType(newType);
    const label = CHART_LABELS[newType] || newType;
    setChartHistory((prev) => {
      const filtered = prev.filter((h) => h.type !== newType);
      return [{ type: newType, label, timestamp: Date.now() }, ...filtered].slice(0, 5);
    });
  }, []);

  // ---- Build final option with style overrides + dataZoom ----
  const buildFinalOption = useCallback(
    (baseOption: Record<string, any>, styleOverrides: ChartStyleOverrides): Record<string, any> => {
      let option = applyStyleOverrides(baseOption, styleOverrides, currentType);
      option = injectDataZoom(option, currentType);
      return option;
    },
    [currentType]
  );

  // ---- Multi-chart configs ----
  const multiCharts = useMemo(() => {
    if (!chartConfig || !multiMode) return [];
    return generateMultiChartConfigs(chartConfig, params.columns || [], currentType);
  }, [chartConfig, multiMode, currentType, params.columns]);

  // ---- Export handlers ----
  const doExport = useCallback(
    (format: 'png' | 'svg', chartRef: any) => {
      if (!chartRef) {
        message.error('图表实例未就绪');
        return;
      }
      const instance = chartRef.getEchartsInstance?.();
      if (!instance) {
        message.error('无法获取 ECharts 实例');
        return;
      }

      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const filename = `trendata_${currentType}_${ts}`;

      if (format === 'png') {
        const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.png`;
        link.click();
        message.success('PNG 已导出');
      } else {
        const url = instance.getDataURL({ type: 'svg' });
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.svg`;
        link.click();
        message.success('SVG 已导出');
      }
    },
    [currentType]
  );

  const handleExportPNG = () => doExport('png', mainChartRef.current);
  const handleExportSVG = () => doExport('svg', mainChartRef.current);

  // Copy 4K image to clipboard (delegates to shared util)
  const handleCopy4K = useCallback(async (chartIndex: number) => {
    let echartsInstance: any = null;
    if (chartIndex === -1) {
      echartsInstance = mainChartRef.current?.getEchartsInstance();
    } else {
      echartsInstance = multiChartRefs.current[chartIndex]?.getEchartsInstance();
    }
    handleCopy4KUtil(echartsInstance, message);
  }, []);

  // HTML report export
  const handleExportHtml = () => {
    if (!chartConfig) {
      message.warning('请等待图表加载完成');
      return;
    }
    const html = generateHtmlReport(
      chartConfig.title || '智能图表分析',
      buildFinalOption(chartConfig.option, getStyle(-1)),
      explanation,
      params,
    );
    downloadHtml(html, 'smart_chart');
    message.success('HTML 报告已下载');
  };

  // ---- Compatible chart types for quick switch bar ----
  const visibleChartTypes = useMemo(() => {
    if (compatibleTypes.length === 0) {
      return Object.entries(CHART_LABELS).map(([key, label]) => ({
        chart_type: key, display_name: label, compatible: true, confidence: 0.5,
      }));
    }
    return compatibleTypes.filter((ct) => ct.compatible);
  }, [compatibleTypes]);

  // ---- Chart display title ----
  const singleStyle = getStyle(-1);
  const displayTitle = singleStyle.title || chartConfig?.title || '图表分析';

  const colOptions = selectedCols.map(c => ({ value: c, label: c }));

  return (
    <div>
      {/* Column Adjustment Drawer */}
      <Drawer
        title="调整数据列"
        placement="left"
        width={360}
        open={colDrawerOpen}
        onClose={() => setColDrawerOpen(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setColDrawerOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleApplyColChanges}>
              应用更改
            </Button>
          </div>
        }
      >
        {colsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>选择列</span>
                <span style={{ fontSize: 12, color: '#999' }}>
                  已选 {selectedCols.length} / {allColumns.length}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Button size="small" onClick={() => setSelectedCols(allColumns.map(c => c.name))}>全选</Button>
                <Button size="small" onClick={() => setSelectedCols([])}>清空</Button>
              </div>
              <Checkbox.Group
                value={selectedCols}
                onChange={(vals) => setSelectedCols(vals as string[])}
                style={{ width: '100%' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {allColumns.map((col) => {
                    const dtype = col.dtype || 'unknown';
                    const tagColor = TYPE_TAG_COLORS[dtype] || 'default';
                    return (
                      <div
                        key={col.name}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '6px 8px', borderRadius: 6,
                          background: selectedCols.includes(col.name) ? '#f0f5ff' : '#fafafa',
                          border: `1px solid ${selectedCols.includes(col.name) ? '#d6e4ff' : '#f0f0f0'}`,
                        }}
                      >
                        <Checkbox value={col.name} style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{col.name}</span>
                        </Checkbox>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Tag color={tagColor} style={{ fontSize: 10, lineHeight: '18px', margin: 0 }}>{dtype}</Tag>
                          {col.mean !== undefined && (
                            <Tooltip title={`均值: ${col.mean?.toFixed(2)}, 标准差: ${col.std?.toFixed(2)}`}>
                              <span style={{ fontSize: 10, color: '#999', cursor: 'help' }}>x̄={col.mean?.toFixed(1)}</span>
                            </Tooltip>
                          )}
                          {col.unique !== undefined && col.mean === undefined && (
                            <span style={{ fontSize: 10, color: '#999' }}>{col.unique}类</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Checkbox.Group>
            </div>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>轴与分组变量</div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>X 轴</div>
                <Select allowClear placeholder="选择 X 轴列" value={drawerXAxis || undefined}
                  onChange={(val) => setDrawerXAxis(val || '')} options={colOptions} style={{ width: '100%' }} size="small" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Y 轴</div>
                <Select allowClear placeholder="选择 Y 轴列" value={drawerYAxis || undefined}
                  onChange={(val) => setDrawerYAxis(val || '')} options={colOptions} style={{ width: '100%' }} size="small" />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>分组变量</div>
                <Select allowClear placeholder="选择分组列" value={drawerGroupBy || undefined}
                  onChange={(val) => setDrawerGroupBy(val || '')} options={colOptions} style={{ width: '100%' }} size="small" />
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            返回
          </Button>
          <Badge count={selectedCols.length} offset={[-4, 4]} size="small" color="#533afd">
            <Button icon={<EditOutlined />} onClick={handleOpenColDrawer}>
              调整数据列
            </Button>
          </Badge>
        </Space>
        <Space>
          <span style={{ fontSize: 13, color: '#666' }}>多图模式</span>
          <Switch checked={multiMode} onChange={setMultiMode} size="small" />
          <Dropdown
            menu={{
              items: [
                { key: 'png', label: '导出 PNG', icon: <FileImageOutlined />, onClick: handleExportPNG },
                { key: 'svg', label: '导出 SVG', icon: <FileImageOutlined />, onClick: handleExportSVG },
                { type: 'divider' as const },
                { key: 'html', label: '导出 HTML 报告', icon: <FileTextOutlined />, onClick: handleExportHtml },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />}>导出</Button>
          </Dropdown>
        </Space>
      </div>

      {/* Chart history bar */}
      {chartHistory.length > 1 && (
        <div style={{
          display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center',
          background: '#fafbff', padding: '8px 12px', borderRadius: 8,
          border: '1px solid #e8e8f0',
        }}>
          <HistoryOutlined style={{ color: '#533afd', fontSize: 14, marginRight: 4 }} />
          <span style={{ fontSize: 12, color: '#999', marginRight: 8 }}>最近查看:</span>
          {chartHistory.map((h) => (
            <Tooltip key={h.type} title={`切换到${h.label}`}>
              <div
                onClick={() => handleTypeChange(h.type)}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  background: h.type === currentType ? 'linear-gradient(135deg, #533afd, #6b54fd)' : '#fff',
                  color: h.type === currentType ? '#fff' : '#555',
                  border: h.type === currentType ? 'none' : '1px solid #e0e0e0',
                  fontWeight: h.type === currentType ? 600 : 400,
                  transition: 'all 0.2s ease',
                }}
              >
                {h.label}
              </div>
            </Tooltip>
          ))}
        </div>
      )}

      {/* Three-column layout */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Left panel - Chart type switch (vertical list) */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 12, position: 'sticky', top: 80 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: '#061b31', marginBottom: 8, marginTop: 0 }}>图表类型</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' }}>
              {visibleChartTypes.map(ct => (
                <div
                  key={ct.chart_type}
                  onClick={() => handleTypeChange(ct.chart_type)}
                  style={{
                    padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                    background: currentType === ct.chart_type ? '#f0f5ff' : 'transparent',
                    border: currentType === ct.chart_type ? '1px solid #533afd' : '1px solid transparent',
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                    color: currentType === ct.chart_type ? '#533afd' : '#555',
                    fontWeight: currentType === ct.chart_type ? 600 : 400,
                    transition: 'all 0.2s',
                  }}
                >
                  <span style={{ fontSize: 14 }}>{CHART_ICONS[ct.chart_type] || <BarChartOutlined />}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.display_name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Center - Chart + Conclusion */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!multiMode ? (
            /* Single chart mode */
            <div>
              <div className="card-trendata" style={{ padding: 24, position: 'relative' }}>
                <div className="title-bar" style={{ marginBottom: 20 }}>
                  <h3 style={{
                    fontSize: singleStyle.titleFontSize || 18,
                    fontWeight: 600, color: '#061b31', margin: 0,
                  }}>
                    {displayTitle}
                  </h3>
                </div>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 100 }}>
                    <Spin size="large" tip={loadingHint} />
                  </div>
                ) : chartConfig ? (
                  <>
                    {isDemoMode && (
                      <div style={{ background: '#fef8e8', borderLeft: '4px solid #e6a200', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#7a5500' }}>
                        Warning: 当前显示为示例图表（API 连接失败），请检查数据文件是否有效
                      </div>
                    )}
                    <div style={{ overflow: 'hidden', borderRadius: 12 }}>
                      <ReactEChartsCore
                        ref={mainChartRef}
                        option={buildFinalOption(chartConfig.option, singleStyle)}
                        style={{ height: Math.min(chartConfig.option?._chart_height || 400, 600), width: '100%' }}
                        notMerge={true}
                      />
                    </div>
                    {/* Action buttons */}
                    <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, zIndex: 10 }}>
                      <Tooltip title="编辑样式">
                        <Button size="small" shape="circle" icon={<EditOutlined />}
                          onClick={() => setEditingChart(-1)}
                          style={chartActionBtnStyle} />
                      </Tooltip>
                      <Tooltip title="复制 4K 图片">
                        <Button size="small" shape="circle" icon={<CopyOutlined />}
                          onClick={() => handleCopy4K(-1)}
                          style={chartActionBtnStyle} />
                      </Tooltip>
                    </div>
                  </>
                ) : null}
                {/* K7: Editing overlay - always rendered, controlled by transform */}
                <div style={{
                  position: 'absolute', top: 0, right: 0, bottom: 0,
                  width: 320, zIndex: 20,
                  transform: editingChart === -1 ? 'translateX(0)' : 'translateX(100%)',
                  opacity: editingChart === -1 ? 1 : 0,
                  pointerEvents: editingChart === -1 ? 'auto' : 'none',
                  transition: 'transform 0.3s ease, opacity 0.25s ease',
                  background: 'rgba(255,255,255,0.88)',
                  backdropFilter: 'blur(12px)',
                  borderLeft: '1px solid rgba(102,126,234,0.15)',
                  borderRadius: '0 12px 12px 0',
                  boxShadow: '-4px 0 20px rgba(0,0,0,0.06)',
                  padding: '12px 14px',
                  overflowY: 'auto',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#061b31' }}>编辑样式</span>
                    <Button shape="circle" size="small" icon={<CloseOutlined />}
                      onClick={() => setEditingChart(-999)}
                      style={{ border: 'none', background: 'rgba(0,0,0,0.04)' }} />
                  </div>
                  <ChartStyleEditor
                    style={singleStyle}
                    onChange={(s) => setStyleForChart(-1, s)}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Multi-chart grid mode */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {loading ? (
                <div className="card-trendata" style={{ padding: 24, gridColumn: '1 / -1', textAlign: 'center' }}>
                  <Spin size="large" tip={loadingHint} />
                </div>
              ) : (
                multiCharts.map((mc, idx) => {
                  const chartStyle = getStyle(idx);
                  const chartTitle = chartStyle.title || mc.title;
                  return (
                    <div key={idx} className="card-trendata" style={{ padding: 16, position: 'relative' }}>
                      <div className="title-bar" style={{ marginBottom: 12 }}>
                        <h3 style={{
                          fontSize: (chartStyle.titleFontSize || 18) - 2,
                          fontWeight: 600, color: '#061b31', margin: 0,
                        }}>
                          {chartTitle}
                        </h3>
                      </div>
                      <div style={{ overflow: 'hidden', borderRadius: 12 }}>
                        <ReactEChartsCore
                          ref={(el: any) => { if (el) multiChartRefs.current[idx] = el; }}
                          option={buildFinalOption(mc.option, chartStyle)}
                          style={{ height: 280, width: '100%' }}
                          notMerge={true}
                        />
                      </div>
                      {/* Action buttons */}
                      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, zIndex: 10 }}>
                        <Tooltip title="编辑样式">
                          <Button size="small" shape="circle" icon={<EditOutlined />}
                            onClick={() => setEditingChart(idx)}
                            style={chartActionBtnStyle} />
                        </Tooltip>
                        <Tooltip title="复制 4K 图片">
                          <Button size="small" shape="circle" icon={<CopyOutlined />}
                            onClick={() => handleCopy4K(idx)}
                            style={chartActionBtnStyle} />
                        </Tooltip>
                      </div>
                      {/* K7: Editing overlay - always rendered, transform-controlled */}
                      <div style={{
                        position: 'absolute', top: 0, right: 0, bottom: 0,
                        width: 'min(320px, 100%)', zIndex: 20,
                        transform: editingChart === idx ? 'translateX(0)' : 'translateX(100%)',
                        opacity: editingChart === idx ? 1 : 0,
                        pointerEvents: editingChart === idx ? 'auto' : 'none',
                        transition: 'transform 0.3s ease, opacity 0.25s ease',
                        background: 'rgba(255,255,255,0.88)',
                        backdropFilter: 'blur(12px)',
                        borderLeft: '1px solid rgba(102,126,234,0.15)',
                        borderRadius: '0 12px 12px 0',
                        boxShadow: '-4px 0 20px rgba(0,0,0,0.06)',
                        padding: '12px 14px',
                        overflowY: 'auto',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#061b31' }}>编辑样式</span>
                          <Button shape="circle" size="small" icon={<CloseOutlined />}
                            onClick={() => setEditingChart(-999)}
                            style={{ border: 'none', background: 'rgba(0,0,0,0.04)' }} />
                        </div>
                        <ChartStyleEditor
                          style={chartStyle}
                          onChange={(s) => setStyleForChart(idx, s)}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Right panel - AI explanation */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ position: 'sticky', top: 80 }}>
            <AIPanel explanation={explanation} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Demo chart generator ----
function generateDemoChart(type: string): Record<string, any> {
  const categories = ['一月', '二月', '三月', '四月', '五月', '六月'];
  const data1 = [820, 932, 901, 934, 1290, 1330];
  const data2 = [620, 732, 801, 834, 1090, 1130];

  const baseStyle = {
    color: ['#533afd', '#6b54fd', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'],
  };

  switch (type) {
    case 'pie':
      return {
        ...baseStyle,
        tooltip: { trigger: 'item' },
        legend: { bottom: 10 },
        series: [{
          type: 'pie', radius: ['40%', '70%'], avoidLabelOverlap: true,
          itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
          data: categories.map((name, i) => ({ name, value: data1[i] })),
        }],
      };
    case 'line':
      return {
        ...baseStyle,
        tooltip: { trigger: 'axis' },
        legend: { data: ['系列1', '系列2'] },
        xAxis: { type: 'category', data: categories },
        yAxis: { type: 'value' },
        series: [
          { name: '系列1', type: 'line', data: data1, smooth: true },
          { name: '系列2', type: 'line', data: data2, smooth: true },
        ],
      };
    case 'scatter':
      return {
        ...baseStyle,
        tooltip: { trigger: 'item' },
        xAxis: { type: 'value', name: 'X' },
        yAxis: { type: 'value', name: 'Y' },
        series: [{ type: 'scatter', data: data1.map((v, i) => [v, data2[i]]), symbolSize: 12 }],
      };
    case 'boxplot':
      return {
        ...baseStyle,
        tooltip: { trigger: 'item' },
        xAxis: { type: 'category', data: ['数据集'] },
        yAxis: { type: 'value' },
        series: [{ type: 'boxplot', data: [[620, 820, 932, 1090, 1330]] }],
      };
    case 'histogram':
      return {
        ...baseStyle,
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: ['0-200', '200-400', '400-600', '600-800', '800-1000', '1000+'] },
        yAxis: { type: 'value', name: '频次' },
        series: [{ type: 'bar', data: [5, 12, 28, 35, 22, 8], barWidth: '80%' }],
      };
    case 'heatmap':
      return {
        tooltip: {},
        xAxis: { type: 'category', data: categories.slice(0, 4) },
        yAxis: { type: 'category', data: ['A', 'B', 'C', 'D'] },
        visualMap: { min: 0, max: 100, calculable: true, orient: 'horizontal', left: 'center', bottom: 10 },
        series: [{
          type: 'heatmap',
          data: Array.from({ length: 16 }, (_, i) => [i % 4, Math.floor(i / 4), Math.round(Math.random() * 100)]),
        }],
      };
    case 'radar':
      return {
        ...baseStyle,
        radar: { indicator: categories.map((name) => ({ name, max: 1500 })) },
        series: [{ type: 'radar', data: [{ value: data1, name: '系列1' }, { value: data2, name: '系列2' }] }],
      };
    case 'area':
      return {
        ...baseStyle,
        tooltip: { trigger: 'axis' },
        legend: { data: ['系列1', '系列2'] },
        xAxis: { type: 'category', data: categories },
        yAxis: { type: 'value' },
        series: [
          { name: '系列1', type: 'line', data: data1, smooth: true, areaStyle: { opacity: 0.3 } },
          { name: '系列2', type: 'line', data: data2, smooth: true, areaStyle: { opacity: 0.3 } },
        ],
      };
    case 'pareto': {
      const total = data1.reduce((a, b) => a + b, 0);
      let running = 0;
      const cumulative = data1.map((v) => { running += v; return Math.round((running / total) * 100); });
      return {
        ...baseStyle,
        tooltip: { trigger: 'axis' },
        legend: { data: ['数值', '累计百分比'] },
        xAxis: { type: 'category', data: categories },
        yAxis: [{ type: 'value', name: '数值' }, { type: 'value', name: '累计 %', max: 100 }],
        series: [
          { name: '数值', type: 'bar', data: data1 },
          { name: '累计百分比', type: 'line', yAxisIndex: 1, data: cumulative },
        ],
      };
    }
    case 'funnel':
      return {
        ...baseStyle,
        tooltip: { trigger: 'item' },
        legend: { bottom: 10 },
        series: [{
          type: 'funnel', left: '10%', width: '80%', sort: 'descending', gap: 2,
          data: categories.map((name, i) => ({ name, value: data1[i] })),
        }],
      };
    case 'treemap':
      return {
        ...baseStyle,
        tooltip: { trigger: 'item' },
        series: [{
          type: 'treemap',
          data: categories.map((name, i) => ({ name, value: data1[i] })),
          leafDepth: 1, label: { show: true, formatter: '{b}\n{c}' }, breadcrumb: { show: false },
        }],
      };
    case 'bubble':
      return {
        ...baseStyle,
        tooltip: { trigger: 'item' },
        xAxis: { type: 'value', name: 'X' },
        yAxis: { type: 'value', name: 'Y' },
        series: [{ type: 'scatter', data: data1.map((v, i) => [v, data2[i], Math.round(v * 0.1)]), symbolSize: 20 }],
      };
    case 'candlestick':
      return {
        ...baseStyle,
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: categories },
        yAxis: { type: 'value' },
        series: [{ type: 'candlestick', data: data1.map((v) => [v, v + 50, v - 30, v + 80]) }],
      };
    default: // bar
      return {
        ...baseStyle,
        tooltip: { trigger: 'axis' },
        legend: { data: ['系列1', '系列2'] },
        xAxis: { type: 'category', data: categories },
        yAxis: { type: 'value' },
        series: [
          { name: '系列1', type: 'bar', data: data1, barWidth: '30%' },
          { name: '系列2', type: 'bar', data: data2, barWidth: '30%' },
        ],
      };
  }
}

export default SmartChartResult;
